import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Upload, FileText, Edit3, Trash2, Download, Plus, Search, Filter, EyeOff, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { Layout } from "@/components/Layout";

interface ClientDocument {
  id: string;
  title: string;
  description: string | null;
  file_name: string;
  file_path: string;
  file_size: number | null;
  category: string;
  tags: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function ClientDocsManager() {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editingDoc, setEditingDoc] = useState<ClientDocument | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const { canAccessAdmin: canAccessAdminRole, user, staffMember, loading: authLoading } = useAuth();
  // Investment team members manage client documents alongside admins.
  const canAccessAdmin = () => canAccessAdminRole() || staffMember?.team === 'Investment';

  const [newDoc, setNewDoc] = useState({
    title: "",
    description: "",
    category: "general",
    tags: "",
    file: null as File | null,
  });

  useEffect(() => {
    if (!canAccessAdmin()) {
      console.log('ClientDocsManager: Access check failed', { 
        user: user?.email, 
        staffMember: staffMember ? {
          email: staffMember.email,
          role: staffMember.role,
          role_level: staffMember.role_level
        } : null,
        authLoading,
        isAuthenticated: !!user && !!staffMember
      });
      
      if (!authLoading) {
        toast.error("Access denied. Admin privileges required.");
        return;
      }
    } else {
      console.log('ClientDocsManager: Access granted', { 
        user: user?.email, 
        staffMember: staffMember ? {
          email: staffMember.email,
          role: staffMember.role,
          role_level: staffMember.role_level
        } : null
      });
      fetchDocuments();
    }
  }, [canAccessAdmin, authLoading, user, staffMember]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('client_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoc.file || !newDoc.title) {
      toast.error('Please provide a title and select a file');
      return;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}-${newDoc.file.name}`;
      const filePath = `documents/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('client-documents')
        .upload(filePath, newDoc.file);

      if (uploadError) throw uploadError;

      // Save document metadata
      const { error: dbError } = await supabase
        .from('client_documents')
        .insert({
          title: newDoc.title,
          description: newDoc.description || null,
          file_name: newDoc.file.name,
          file_path: filePath,
          file_size: newDoc.file.size,
          category: newDoc.category,
          tags: newDoc.tags ? newDoc.tags.split(',').map(tag => tag.trim()) : null,
        });

      if (dbError) throw dbError;

      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setNewDoc({ title: "", description: "", category: "general", tags: "", file: null });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (doc: ClientDocument) => {
    try {
      // Find the original doc to check if file_name changed
      const originalDoc = documents.find(d => d.id === doc.id);
      let newFilePath = doc.file_path;

      if (originalDoc && originalDoc.file_name !== doc.file_name) {
        // Rename the actual file in storage: copy to new path, delete old
        const folderPath = doc.file_path.substring(0, doc.file_path.lastIndexOf('/') + 1);
        const newStorageName = `${Date.now()}-${doc.file_name}`;
        newFilePath = `${folderPath}${newStorageName}`;

        // Download the old file
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('client-documents')
          .download(doc.file_path);

        if (downloadError) throw downloadError;

        // Upload with new name
        const { error: uploadError } = await supabase.storage
          .from('client-documents')
          .upload(newFilePath, fileData);

        if (uploadError) throw uploadError;

        // Delete old file
        await supabase.storage
          .from('client-documents')
          .remove([doc.file_path]);
      }

      const { error } = await supabase
        .from('client_documents')
        .update({
          title: doc.title,
          description: doc.description,
          category: doc.category,
          tags: doc.tags,
          is_active: doc.is_active,
          file_name: doc.file_name,
          file_path: newFilePath,
        })
        .eq('id', doc.id);

      if (error) throw error;

      toast.success('Document updated successfully');
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const handleToggleVisibility = async (doc: ClientDocument) => {
    try {
      const { error } = await supabase
        .from('client_documents')
        .update({ is_active: !doc.is_active })
        .eq('id', doc.id);

      if (error) throw error;

      toast.success(doc.is_active ? 'Document hidden' : 'Document visible again');
      fetchDocuments();
    } catch (error) {
      console.error('Error toggling document visibility:', error);
      toast.error('Failed to update document visibility');
    }
  };

  const handleDelete = async (doc: ClientDocument) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('client-documents')
        .remove([doc.file_path]);

      if (storageError) console.warn('Storage delete error:', storageError);

      // Delete from database
      const { error: dbError } = await supabase
        .from('client_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc: ClientDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-documents')
        .download(doc.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.file_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(documents.map(doc => doc.category)));

  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
              <CardDescription>
                Checking your permissions...
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!canAccessAdmin()) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Access Denied</CardTitle>
              <CardDescription>
                You need admin privileges to access this page.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Client Documents Manager</h1>
            <p className="text-muted-foreground">Upload and manage PDF documents for clients</p>
          </div>
          
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Upload New Document</DialogTitle>
                <DialogDescription>
                  Upload a PDF document that will be available to authenticated users.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleFileUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Document Title</Label>
                  <Input
                    id="title"
                    value={newDoc.title}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter document title"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={newDoc.description}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the document"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={newDoc.category}
                    onValueChange={(value) => setNewDoc(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="forms">Forms</SelectItem>
                      <SelectItem value="agreements">Agreements</SelectItem>
                      <SelectItem value="reports">Reports</SelectItem>
                      <SelectItem value="guides">Guides</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (Optional)</Label>
                  <Input
                    id="tags"
                    value={newDoc.tags}
                    onChange={(e) => setNewDoc(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">PDF File</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setNewDoc(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                    required
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={uploading}>
                    {uploading ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents ({filteredDocuments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading documents...</div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {documents.length === 0 ? "No documents uploaded yet" : "No documents match your search"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>File Name</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">
                          <div>
                            <div>{doc.title}</div>
                            {doc.description && (
                              <div className="text-sm text-muted-foreground">
                                {doc.description.length > 50 
                                  ? `${doc.description.substring(0, 50)}...` 
                                  : doc.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {doc.category.charAt(0).toUpperCase() + doc.category.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{doc.file_name}</TableCell>
                        <TableCell>
                          {doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={doc.is_active ? "default" : "secondary"}>
                            {doc.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(doc.created_at), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingDoc(doc)}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleVisibility(doc)}
                              title={doc.is_active ? "Hide document" : "Show document"}
                            >
                              {doc.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(doc)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Document</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{doc.title}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(doc)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {editingDoc && (
        <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingDoc.title}
                  onChange={(e) => setEditingDoc(prev => prev ? { ...prev, title: e.target.value } : null)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>File Name</Label>
                <Input
                  value={editingDoc.file_name}
                  onChange={(e) => setEditingDoc(prev => prev ? { ...prev, file_name: e.target.value } : null)}
                  placeholder="document.pdf"
                />
                <p className="text-xs text-muted-foreground">Changing this will rename the actual stored file.</p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingDoc.description || ''}
                  onChange={(e) => setEditingDoc(prev => prev ? { ...prev, description: e.target.value } : null)}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={editingDoc.category}
                  onValueChange={(value) => setEditingDoc(prev => prev ? { ...prev, category: value } : null)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="forms">Forms</SelectItem>
                    <SelectItem value="agreements">Agreements</SelectItem>
                    <SelectItem value="reports">Reports</SelectItem>
                    <SelectItem value="guides">Guides</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={editingDoc.is_active}
                  onChange={(e) => setEditingDoc(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
                />
                <Label htmlFor="active">Active (visible to users)</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingDoc(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleUpdate(editingDoc)}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
         </Dialog>
       )}
     </div>
     </Layout>
   );
 }